import requiredBackendLevel from './requiredBackendLevel.json';

/**
 * The backend level this web build needs. mf-functions records the level it
 * deployed at `global/backend` (its `backend-level.json`, written by the
 * postdeploy hook). Raise the number in `requiredBackendLevel.json` in the web
 * change that depends on a new or changed callable, trigger or rule, after
 * raising it in mf-functions. `scripts/build.js` reads the same file.
 */
export const REQUIRED_BACKEND_LEVEL: number = requiredBackendLevel.level;

export const BACKEND_INFO_PATH = 'global/backend';

/** `global/backend`, written only by the mf-functions postdeploy hook. */
export interface BackendInfo {
    level: number;
    commit?: string;
    deployedAt?: string;
}

/** The stored node, or null when it is absent or malformed. */
export const parseBackendInfo = (value: unknown): BackendInfo | null => {
    if (!value || typeof value !== 'object') return null;
    const { level, commit, deployedAt } = value as Record<string, unknown>;
    if (typeof level !== 'number' || !Number.isInteger(level)) return null;
    return {
        level,
        commit: typeof commit === 'string' ? commit : undefined,
        deployedAt: typeof deployedAt === 'string' ? deployedAt : undefined,
    };
};

/**
 * True when the instance runs a lower backend level than this build needs.
 * A backend deployed before the hook existed has no node and counts as level 0.
 * A higher level is fine: the backend stays compatible with older clients.
 */
export const isBackendOutdated = (info: BackendInfo | null, required: number = REQUIRED_BACKEND_LEVEL): boolean =>
    (info?.level ?? 0) < required;
